import { css } from 'styled-components';

/**
 * THE-876 — ONE standard guide-card width, for every sideways carousel of
 * guide cards: hub "New guides", hub "Guides picked for your people", the
 * homepage + People-tab "Trending gift guides" rail, the recipient board's
 * "Gift guides" rail (same shared `GuidePeekRail` as the two above), the
 * search page's Guides row (THE-875 consumes this constant directly), and
 * the collection tray.
 *
 * Before this, every row invented its own flex-basis formula against its own
 * track's content box, so the "same" card measured 384/375/396/narrower-still
 * across surfaces at the same viewport (Kate, 2026-10-07) even though every
 * row is built from the same two card components (`GuidePeekCard`,
 * `HubGuideCollageCard`). A fixed width — not a percentage of each track's
 * own padding — is what makes every row agree without each one re-deriving
 * the algebra GuidePeekRail's old comment walked through.
 *
 * DESKTOP IS TODAY'S "New guides" WIDTH (Kate's explicit pick). MOBILE reuses
 * THE-870's occasion-tile peek fraction (69vw, "roughly a third of the next
 * card peeks") rather than inventing a second ratio — see HubOccasionTiles'
 * Track for the derivation of why 69 and not 82.
 *
 * The ONE exception is the "All gift guides" grid at the bottom of the hub
 * (`HubMoreGuidesSection`): it has no carousel and no peek, so it is not a
 * consumer of this module at all — its cards fill their grid column and come
 * out WIDER than this standard on purpose (PRD "Rows: mobile vs. desktop").
 */
export const GUIDE_CARD_DESKTOP_WIDTH_PX = 384;
export const GUIDE_CARD_MOBILE_PEEK_VW = 69;
export const GUIDE_CARD_WIDTH_BREAKPOINT_PX = 640;

/** Sizes a flex-row cell to the standard card width. Callers own the track's
 *  `display: flex` + gap + scroll-snap; this owns only the column width, the
 *  same split `GuidePeekRail`'s old per-surface formulas used to own alone. */
export const GUIDE_CARD_ROW_WIDTH_CSS = css`
  flex: 0 0 auto;
  width: ${GUIDE_CARD_MOBILE_PEEK_VW}vw;
  max-width: 320px;
  @media (min-width: ${GUIDE_CARD_WIDTH_BREAKPOINT_PX}px) {
    width: ${GUIDE_CARD_DESKTOP_WIDTH_PX}px;
    max-width: none;
  }
`;

/**
 * Reserves exactly two title lines, clamped with an ellipsis beyond that
 * (THE-876 rule 4: "every card reserves 2 lines so a row lines up").
 *
 * `em`-based, not a per-breakpoint px table, so it tracks whichever font-size
 * the caller's own `Title` steps to at each breakpoint without this module
 * needing to know those sizes.
 *
 * THIS SUPERSEDES THE OLD "no fixed title-height reserve" rule (THE-183):
 * that rule existed because a min-height WITHOUT a clamp let a 3-line title
 * overflow its reserved box and break the row's baseline — 32 of 65 registry
 * titles needed a third line at the narrowest rail width then in use. Pairing
 * the reserve with `-webkit-line-clamp` closes that hole: no title can ever
 * render past 2 lines any more, so the reserve can no longer be silently
 * outgrown.
 */
export const GUIDE_CARD_TITLE_CLAMP_CSS = css`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * The white, elevated card chrome a `GuidePeekCard`-based row takes on once
 * it opts into `titleBelow` (THE-894): image on top, a white rounded fill
 * lifted with a soft shadow, and the title padding that moves from the bare
 * card's own spacing to this one's gutters. The collection
 * tray originated this treatment; "Guides picked for your people" shares it
 * now rather than forking its own copy, which is the drift THE-894 found —
 * the row used the bare, unstyled card instead.
 *
 * The card ends right after its title: no reserved second title line and no
 * per-guide gift count under it (Kate 2026-10-19 — both read as blank rows
 * under every one-line title). Both rows that use it are flex rows, so their
 * cards still line up: flex items stretch to the tallest card in the row.
 */
export const GUIDE_CARD_ELEVATED_CSS = css`
  border-radius: 16px;
  background: white;
  overflow: hidden;
  padding: 0;
  box-shadow: 0 1px 2px #2a211c14, 0 6px 18px #2a211c0f;
  > div {
    border-radius: 0;
  }
  h3 {
    margin: 0;
    padding: 14px 18px 0;
    text-wrap: initial;
  }
  h3 [data-guide-title] {
    min-height: 0;
  }
  h3:last-child {
    padding-bottom: 16px;
  }
`;
